import { Dispatch, Fragment, ReactNode, SetStateAction, useRef, useState } from 'react';
import classNames from 'classnames';

import iconChevronDown from '../../assets/img/icon-chevron.svg';
import ClickableDiv from '../ClickableDiv/ClickableDiv';
import useAnimatedMount, { AnimatedMountStatus } from '../hooks/useAnimatedMount';
import useClickOutside from '../hooks/useClickOutside';
import useIsMobile from '../hooks/useIsMobile';
import MobileDrawer from '../MobileDrawer/MobileDrawer';

import styles from './CommonDropdown.module.scss';

export type CommonDropdownOption = {
  id: string;
  label: string;
  icon?: string;
  sectionLabel?: string;
  badge?: string;
  badgeClassName?: string;
  disabled?: boolean;
  subText?: string;
};

export type CommonDropdownProps = {
  // Optional: pass both to control open state from the parent (e.g. to coordinate
  // with another dropdown). Omit both to let the component manage its own state.
  isOpen?: boolean;
  setIsOpen?: Dispatch<SetStateAction<boolean>>;
  options: CommonDropdownOption[];
  selectedOption: CommonDropdownOption;
  onSelectOption: (option: CommonDropdownOption) => void;
  title?: string;
  iconClassName?: string;
  footer?: ReactNode;
  isDisabled?: boolean;
  menuClassName?: string;
  itemClassName?: string;
  chevronIcon?: string;
  id?: string; // for testing purposes
};

const CommonDropdown = ({
  isOpen: isOpenProp,
  setIsOpen: setIsOpenProp,
  options,
  selectedOption,
  onSelectOption,
  title,
  iconClassName,
  footer,
  isDisabled,
  menuClassName,
  itemClassName,
  chevronIcon,
  id,
}: CommonDropdownProps) => {
  const { isMobile } = useIsMobile();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isOpenProp ?? internalOpen;
  const setIsOpen = setIsOpenProp ?? setInternalOpen;
  const { isMounted: isMenuMounted, status: menuStatus } = useAnimatedMount(isOpen);
  useClickOutside(dropdownRef, () => setIsOpen(false));

  const renderOptions = () => (
    <>
      {options.map(option => (
        <Fragment key={option.id}>
          {option.sectionLabel && (
            <div className={classNames(
              styles.dropdownMenuItem,
              styles.nonClickableOption,
              GlobalClassesEnum.CommonBody1Text,
              GlobalClassesEnum.CommonTextSecondary,
            )}
            >
              {option.sectionLabel}
            </div>
          )}
          <ClickableDiv
            className={classNames(
              GlobalClassesEnum.CommonBody1Text,
              styles.dropdownMenuItem,
              itemClassName,
              {
                [styles.selectedOption]: option.id === selectedOption.id,
                [styles.dropdownMenuItemDisabled]: option.disabled,
              },
            )}
            onClick={() => {
              if (option.disabled) {
                return;
              }
              onSelectOption(option);
              setIsOpen(false);
            }}
          >
            {option.icon && (
              <img
                src={option.icon}
                alt={option.label}
                className={classNames(styles.dropdownIcon, iconClassName)}
              />
            )}
            <div className={styles.optionTextWrapper}>
              <div>{option.label}</div>
              {option.subText && (
                <div className={classNames(GlobalClassesEnum.CommonBody2Text, GlobalClassesEnum.CommonTextSecondary)}>
                  {option.subText}
                </div>
              )}
            </div>
            {option.badge && (
              <span className={classNames(styles.badge, option.badgeClassName)}>{option.badge}</span>
            )}
          </ClickableDiv>
        </Fragment>
      ))}
    </>
  );

  return (
    <div className={styles.wrapper} ref={dropdownRef} id={id}>
      <ClickableDiv
        className={classNames(styles.content, {
          [styles.contentOpen]: isOpen,
          [styles.contentDisabled]: isDisabled,
        })}
        onClick={() => !isDisabled && setIsOpen(open => !open)}
      >
        <div className={GlobalClassesEnum.CommonBody1Text}>
          {selectedOption.label}
        </div>
        <img
          src={chevronIcon ?? iconChevronDown}
          alt="Select"
          className={classNames(styles.dropdownChevron, { [styles.dropdownChevronOpen]: isOpen })}
        />
      </ClickableDiv>
      {!isMobile && !isDisabled && isMenuMounted && (
        <div className={classNames(
          styles.dropdownMenu,
          menuClassName,
          menuStatus === AnimatedMountStatus.Open ? styles.dropdownMenuOpen : styles.dropdownMenuClosing,
        )}
        >
          {renderOptions()}
          {footer}
        </div>
      )}
      {isMobile && !isDisabled && (
        <MobileDrawer
          title={title ?? ''}
          isOpen={isOpen}
          onCloseStart={() => setIsOpen(false)}
          onClose={() => setIsOpen(false)}
        >
          <div>
            {renderOptions()}
            {footer}
          </div>
        </MobileDrawer>
      )}
    </div>
  );
};

export default CommonDropdown;
